import { useEffect, useState } from "react";
import { HeartHandshake, Sparkles } from "lucide-react";
import { api, AUTH_URL } from "@/lib/api";
import { DECOR_AVATARS } from "@/lib/blessed";

const FloatingAvatar = ({ src, size, top, left, right, badge, badgeColor, delay, emoji }) => (
  <div className="absolute animate-float" style={{ top, left, right, animationDelay: delay }}>
    <div className="relative">
      <img src={src} alt="" className="rounded-pill object-cover border-4 border-white/90 shadow-xl" style={{ width: size, height: size }} referrerPolicy="no-referrer" />
      {badge && (
        <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-2.5 py-1 rounded-pill text-xs font-black text-white shadow-lg whitespace-nowrap" style={{ background: badgeColor }}>
          {badge}
        </span>
      )}
      {emoji && <span className="absolute -bottom-2 -right-2 text-2xl">{emoji}</span>}
    </div>
  </div>
);

export default function Login() {
  const [today, setToday] = useState(0);

  useEffect(() => {
    api.get("/stats/public").then(({ data }) => setToday(data?.gifts_today ?? 0)).catch(() => {});
  }, []);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    const target = `${AUTH_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
    // Navigate the TOP window in case the app is running inside a preview iframe.
    try {
      if (window.top && window.top !== window.self) window.top.location.href = target;
      else window.location.href = target;
    } catch (e) {
      window.location.href = target;
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden" style={{ background: "linear-gradient(180deg,#1E3AA8 0%,#1E40AF 45%,#182F86 100%)" }}>
      {/* solid playful blobs */}
      <div className="absolute rounded-pill" style={{ width: 460, height: 460, top: -120, right: -80, background: "#8B8172", opacity: 0.75 }} />
      <div className="absolute rounded-pill" style={{ width: 420, height: 420, top: 360, right: -40, background: "#2E4FB8", opacity: 0.6, filter: "blur(2px)" }} />
      <div className="absolute rounded-pill" style={{ width: 380, height: 380, bottom: -80, left: -120, background: "#6B4A55", opacity: 0.7 }} />

      {/* floating avatars */}
      <FloatingAvatar src={DECOR_AVATARS[0]} size={92} top="18%" left="4%" badge="+$25" badgeColor="#FFD93D" delay="0s" />
      <FloatingAvatar src={DECOR_AVATARS[1]} size={120} top="8%" right="12%" emoji="❤️" delay="1.2s" />
      <FloatingAvatar src={DECOR_AVATARS[2]} size={104} top="52%" right="6%" badge="+$50" badgeColor="#5B21B6" delay="0.6s" />
      <FloatingAvatar src={DECOR_AVATARS[3]} size={70} top="60%" left="30%" emoji="🎉" delay="1.8s" />

      <div className="relative z-10 min-h-screen max-w-xl mx-auto px-6 flex flex-col">
        {/* top badge */}
        <div className="pt-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-pill bg-white/12 backdrop-blur-md">
            <span className="w-2.5 h-2.5 rounded-pill" style={{ background: "#FFD93D" }} />
            <span className="text-white font-black">Blessed</span>
            <span className="ml-1 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-pill bg-black/25 text-white text-xs font-bold">
              <span className="w-1.5 h-1.5 rounded-pill bg-white" /> LIVE
            </span>
          </div>
        </div>

        {/* hero */}
        <div className="flex-1 flex flex-col justify-center py-10">
          <div className="text-sm font-black tracking-[0.35em] mb-3" style={{ color: "#FFD93D" }}>TAP. GIVE. GLOW.</div>
          <h1 className="text-white font-black leading-[0.95] tracking-tight" style={{ fontSize: "clamp(56px, 15vw, 88px)" }}>
            Kindness
            <br />
            <span style={{ color: "#FFD93D" }}>on the map.</span>
          </h1>
          <p className="text-white/90 font-semibold text-lg mt-6 max-w-md leading-snug">
            See real people near you who need a hand — and help with one tap. It feels amazing. Try it.
          </p>
        </div>

        {/* bottom actions */}
        <div className="pb-8">
          <button
            data-testid="google-signin-button"
            onClick={signIn}
            className="w-full h-16 rounded-pill bg-white flex items-center justify-center gap-3 shadow-2xl active:scale-[0.98] transition-transform"
          >
            <svg width="26" height="26" viewBox="0 0 48 48">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
            </svg>
            <span className="text-slate-900 font-black text-xl">Continue with Google</span>
            <Sparkles size={22} className="text-brand-primary" />
          </button>

          <div className="flex justify-center gap-3 mt-5">
            <span className="inline-flex items-center gap-2 px-4 py-2.5 rounded-pill bg-white/12 backdrop-blur-md text-white text-sm font-bold">
              🎉 {today} gifts today
            </span>
            <span className="inline-flex items-center gap-2 px-4 py-2.5 rounded-pill bg-white/12 backdrop-blur-md text-white text-sm font-bold">
              🤝 Stripe secure
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
